import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';
import FormField from './FormField';
import SuburbSelector from './SuburbSelector';
import ChangePassword from './ChangePassword';
import GetNameBadge from './GetNameBadge';
import MemberLinks from './MemberLinks';
import CovidNotice from './CovidNotice';
import GeneralNotice from './GeneralNotice';

import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';

import clsx from 'clsx';
import useStyles from '../styles/Styles';

import { loadSettings, loadMembersInfo, loadMember, updateMember, resetErrorMessage } from '../actions';
import _ from 'lodash';
import {isTrue, landlineMask, mobileMask} from '../utils';
import * as yup from 'yup';
import dayjs from 'dayjs';

const Member = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const user = useSelector(state => state.user);
	const member = useSelector(state => state.member.data);
	
	const generalSettings = useSelector(state => state.settings.general.data);
	const memberSettings = useSelector(state => state.settings.members.data);
	const membershipTypes = useSelector(state => state.settings.membership_types.data);
	const classificationTypes = useSelector(state => state.settings.classifier_classification.data);
	const classifier1Types = useSelector(state => state.settings.classifier_classifier1.data);
	const classifier2Types = useSelector(state => state.settings.classifier_classifier2.data);
	const classifier3Types = useSelector(state => state.settings.classifier_classifier3.data);
	const memberLabels = useSelector(state => state.settings.labels_members.data);
	
	const fetchingMember = useSelector(state => state.member.fetching);
	const updatingMember = useSelector(state => state.member.updating);
	
	const [passwordOpen, setPasswordOpen] = useState(false);
	const [nameBadgeOpen, setNameBadgeOpen] = useState(false);
	
	const [badgeAllowed, setBadgeAllowed] = useState(false);
	useEffect(() => {
		if(isTrue(member.active) && memberSettings.allow_badge_printing){
			setBadgeAllowed(true);
		} else {
			setBadgeAllowed(false);
		}
	}, [member, memberSettings])
	
	const [submitted, setSubmitted] = useState(false);
	useEffect(() => {
		if(updatingMember){
			setSubmitted(true);
		} 
	}, [updatingMember])
	
	useEffect(() => {
		if(user.member_no > 0){
			dispatch(loadMember());
			dispatch(loadSettings('members'));
			dispatch(loadMembersInfo('membership_types'));
			dispatch(loadMembersInfo('general'));
		}
	}, [dispatch, user])
	
	const [data, setData] = useState({
		member_no: 0,
		active: false,
		early_enrol: 0,
		membership_expiry_date: "",
		join_year: 0,
		join_date: "",
		date_activated: "",
		date_inactivated: "",
		gender: "",
		surname: "",
		first_name: "",
		preferred_name: "",
		address1: "",
		street: "",
		suburb: "",
		postcode: "",
		country: "",
		state: "",
		phone_home: "",
		phone_business: "",
		mobile: "",
		email: "",
		employer: "",
		fax: "",
		member_type: "",
		occupation: "",
		registration: "",
		registration_date: "",
		classification: "",
		classifier1: "",
		classifier2: "",
		classifier3: "",
		spare_field: "",
		checkbox1: false,
		checkbox2: false,
		checkbox3: false,
		checkbox4: false,
		birth_year: 0,
		emergency_contact: "",
		emergency_number: "",
		emergency_relationship: "",
		received_date: "",
		number1: 0,
		number2: 0,
		tickets: 0,
		notes: "",
		vaccinated: false
	});
	
	useEffect(() => {
		setData({...member});
	}, [member])
	

	const [validator, setValidator] = useState({
		gender: {error: false, message: ''},
		surname: {error: false, message: ''},
		first_name: {error: false, message: ''},
		street: {error: false, message: ''},
		suburb: {error: false, message: ''},
		postcode: {error: false, message: ''},
		state: {error: false, message: ''},
		phone_home: {error: false, message: ''},
		phone_business: {error: false, message: ''},
		mobile: {error: false, message: ''},
		email: {error: false, message: ''},
		member_type: {error: false, message: ''},
		occupation: {error: false, message: ''},
		registration: {error: false, message: ''},
		registration_date: {error: false, message: ''},
		classification: {error: false, message: ''},
		classifier1: {error: false, message: ''},
		classifier2: {error: false, message: ''},
		classifier3: {error: false, message: ''},
		birth_year: {error: false, message: ''},
		emergency_contact: {error: false, message: ''},
		emergency_number: {error: false, message: ''},
		emergency_relationship: {error: false, message: ''},
		received_date: {error: false, message: ''},
		number1: {error: false, message: ''},
		number2: {error: false, message: ''},
		tickets: {error: false, message: ''},
	});
	
	const [formStatus, setFormStatus] = useState({
		dirty: false,
		error: false,
		message: ''
	});	
	
	const [memberTypeOptions, setMemberTypeOptions] = useState({'0': 'Select...'});
	useEffect(() => {
		if(membershipTypes){
			setMemberTypeOptions(
				_.chain(membershipTypes)
				.filter({member_can_select: 1}).keyBy('code').mapValues('code').value()
			);
		}
	}, [membershipTypes])
	
	const [classificationOptions, setClassificationOptions] = useState({'0': 'Select...'});
	useEffect(() => {
		if(classificationTypes){
			setClassificationOptions(
				_.chain(classificationTypes).keyBy('code').mapValues('code').value()
			);
		}
	}, [classificationTypes])
	
	const [classifier1Options, setClassifier1Options] = useState({'0': 'Select...'});
	useEffect(() => {
		if(classifier1Types){
			setClassifier1Options(
				_.chain(classifier1Types).keyBy('code').mapValues('code').value()
			);
		}
	}, [classifier1Types])
	
	const [classifier2Options, setClassifier2Options] = useState({'0': 'Select...'});
	useEffect(() => {
		if(classifier2Types){
			setClassifier2Options(
				_.chain(classifier2Types).keyBy('code').mapValues('code').value()
			);
		}
	}, [classifier2Types])
	
	const [classifier3Options, setClassifier3Options] = useState({'0': 'Select...'});
	useEffect(() => {
		if(classifier3Types){
			setClassifier3Options(
				_.chain(classifier3Types).keyBy('code').mapValues('code').value()
			);
		}
	}, [classifier3Types])
	
	const [schemaShape, setSchemaShape] = useState({
		first_name: yup.string().required('This field cannot be empty'),
		surname: yup.string().required('This field cannot be empty'),
		street: yup.string().required('This field cannot be empty'),
		suburb: yup.string().required('This field cannot be empty'),
		postcode: yup.string().matches(/^[0-9]{4}$/, {message: 'Please enter a 4-digit postcode'}),
		state: yup.string().required('Please make a selection'),
		phone_home: yup.string().matches(/^[(](\d{2})[)] (\d{4})-(\d{4})$/, { message: 'Please enter a 10-digit phone number including area code', excludeEmptyString: true }),
		phone_business: yup.string().matches(/^[(](\d{2})[)] (\d{4})-(\d{4})$/, { message: 'Please enter a 10-digit phone number including area code', excludeEmptyString: true }),
		mobile: yup.string().matches(/^(\d{4}) (\d{3}) (\d{3})$/, { message: 'Please enter 10-digit mobile number', excludeEmptyString: true }),
		email: yup.string().required('Please enter a valid email address').email('Please enter a valid email address'),
		birth_year: yup.string().required('This field cannot be empty').matches(/^\d{4}$/, { message: 'Please enter a 4-digit year'})
		.test('valid-birth-year', 'Minimum joining age is 18. Is your birth year correct?',
			value => { 
				const intVal = _.toInteger(value); 
				const maxYear = (new Date()).getFullYear() - 18; 
				return (intVal <= maxYear); 
			}
		),
		gender: yup.string().oneOf(['O', 'M', 'F'], 'Please make a selection').required('Please make a selection'),
		number1: yup.string().matches(/^\d{1,6}$/, { message: 'Please enter a whole number between 0 and 999999', excludeEmptyString: true}),
		number2: yup.string().matches(/^(\d{1,10})(|\.\d{1,2})$/, { message: 'Please enter a number between 0 and 9999999999.99. Up to 2 decimal places are allowed.', excludeEmptyString: true}),
		tickets: yup.string().matches(/^\d{1,6}$/, { message: 'Please enter a whole number between 0 and 999999', excludeEmptyString: true}),
		received_date: yup.date().nullable()
		.min('1900-01-01', 'Minimum allowed date is 01/01/1900')
		.max('2100-01-01', 'Maximum allowed date is 01/01/2100')
		.typeError('Please enter a valid date in the format dd/mm/yyyy'),
		registration_date: yup.date().nullable(
		).min('1900-01-01', 'Minimum allowed date is 01/01/1900')
		.max('2100-01-01', 'Maximum allowed date is 01/01/2100')
		.typeError('Please enter a valid date in the format dd/mm/yyyy'),
		emergency_contact: yup.string().required('This field cannot be empty'),
		emergency_number: yup.string().required('This field cannot be empty'),
	});
	
	useEffect(() => {
		setSchemaShape(s => { return {
			...s,
			occupation: yup.string().test('maybe-require-occupation', 'This field cannot be empty',
				value => isTrue(memberSettings['require_occuption']) && _.isEmpty(value) ? false : true
			),
			registration: yup.string().test('maybe-require-registration', 'This field cannot be empty',
				value => isTrue(memberSettings['require_registration']) && _.isEmpty(value) ? false : true
			),
			registration_date: yup.date().nullable(
			).min('1900-01-01', 'Minimum allowed date is 01/01/1900')
			.max('2100-01-01', 'Maximum allowed date is 01/01/2100')
			.typeError('Please enter a valid date in the format dd/mm/yyyy')
			.test('maybe-require-registration_date', 'This field cannot be empty',
				value => isTrue(memberSettings.require_registration_date) && !(_.isDate(value)) ? false : true
			), 
			emergency_relationship: yup.string().test('maybe-require-emergency_relationship', 'This field cannot be empty',
				value => isTrue(memberSettings['require_emergency_relationship']) && _.isEmpty(value) ? false : true
			), 
		}})
	}, [memberSettings])
	
	useEffect(() => {
		if(isTrue(memberSettings.allow_member_type_change)){
			setSchemaShape(s => { return {
				...s,
				member_type: yup.string().test('valid-member_type', 'Please make a selection', 
					value => _.has(memberTypeOptions, value)
				),
			}})
		}
	}, [memberSettings, memberTypeOptions])
	
	useEffect(() => {
		if(isTrue(memberSettings.show_classification)){
			setSchemaShape(s => { return {
				...s,
				classification: yup.string().transform((value, originalValue) => {
					return _.has(classificationOptions, value ) ? value : ''
				})
			}})
		}
	}, [memberSettings, classificationOptions])
	
	useEffect(() => {
		if(isTrue(memberSettings.show_classifier1)){
			setSchemaShape(s => { return {
				...s,
				classification: yup.string().transform((value, originalValue) => {
					return _.has(classifier1Options, value ) ? value : ''
				})
			}})
		}
	}, [memberSettings, classifier1Options])
	
	useEffect(() => {
		if(isTrue(memberSettings.show_classifier2)){
			setSchemaShape(s => { return {
				...s,
				classification: yup.string().transform((value, originalValue) => {
					return _.has(classifier2Options, value ) ? value : ''
				})
			}})
		}
	}, [memberSettings, classifier2Options])
	
	useEffect(() => {
		if(isTrue(memberSettings.show_classifier3)){
			setSchemaShape(s => { return {
				...s,
				classification: yup.string().transform((value, originalValue) => {
					return _.has(classifier3Options, value ) ? value : ''
				})
			}})
		}
	}, [memberSettings, classifier3Options])
	
	// validator is not in the dependency array because we don't need to update this every time validator changes
	const storeAndValidate = useCallback((key, value) => {
		
		setData(d => { return {...d, [key]: value}});
		setFormStatus((formStatus) => { return {...formStatus, dirty: true}});
		
		let s = yup.object().shape(schemaShape);
		
		if(_.has(validator, key)){
			s.validateAt(key, {[key]: value})
			.then(val => {
				const newVal = {value: value, error: false, message: ''};
				if(key === 'birth_year'){
					const intVal = _.toInteger(value);
					const maxWarn = (new Date()).getFullYear() - 120;
					const minWarn = (new Date()).getFullYear() - memberSettings.minimum_joining_age
					if(intVal < maxWarn){
						newVal.message = 'Warning: It looks like you are over 120 years old. Is your birth year correct?';
					} else if(intVal > minWarn){
						newVal.message = 'Warning: It looks like you are younger than '+memberSettings.minimum_joining_age+'. Our preferred minimum joining age is '+memberSettings.minimum_joining_age+'. Is your birth year correct?';
					}
				} 
				setValidator(v => { return {...v, [key]: newVal}})
			})
			.catch(err => {
				setValidator(v => { return {...v, [key]: {error: true, message: err.message}}})
			})
		} // eslint-disable-next-line
	}, [memberSettings, schemaShape])
	
	const setSuburb = useCallback((suburb) => {
		setData(d => {return {...d, 
			suburb: suburb.suburb,
			state: suburb.state, 
			postcode: suburb.postcode
		}});
		
		setValidator(v => { return {...v, 
			suburb: {error: false, message: ''}, 
			state: {error: false, message: ''}, 
			postcode: {error: false, message: ''}
		}})
	}, [])
	
	const resetForm = () => {
		setData({...member});
		setValidator( _.mapValues(validator, v => {
			return {error: false, message: ''}
		}))
		
		setSubmitted(false);
		setFormStatus({
			dirty: false,
			error: false, 
			message: ''
		});
	}
	
	const fields = [
		{ 
			id: "member_no",
			type: "read-only",
			atts: {
				label: "Member Number",
				display_value: member.member_no || 0,
			},
			show: true,
			send: false
		},
		{ 
			id: "member_type",
			type: memberSettings.allow_member_type_change ? "select" : "read-only",
			atts: {
				label: memberLabels.member_type,
				required: isTrue(memberSettings.allow_member_type_change),
				options: memberTypeOptions,
			},
			show: true,
			send: isTrue(memberSettings.allow_member_type_change),
		},
		{
			id: "active",
			type: "read-only",
			atts: {
				label: "Membership Active",
				display_value: (member.active ? 'Yes' : 'No'),
			},
			show: true,
			send: false, 
		},
		{
			id: "membership_expiry_date",
			type: "read-only",
			atts: {
				label: "Membership Expiry",
				display_value: (member.membership_expiry_date ? dayjs(member.membership_expiry_date).format('D MMM YYYY') : '-'),
			},
			show: true,
			send: false, 
		},
		{ 
			id: "member_type_divider",
			divider: true,
			show: true
		},
		{ 
			id: "first_name",
			atts: {
				label: "First name",
				required: true,
			},
			
			show: true,
			send: true,
		},
		{ 
			id: "surname",
			atts: {
				label: memberLabels.surname,
				required: true,
			},
			
			show: true,
			send: true,
		},
		{ 
			id: "preferred_name",
			atts: {
				label: "Preferred name",
			},
			show: memberSettings.show_preferred_name,
			send: memberSettings.show_preferred_name,
		},
		{ 
			id: "name_divider",
			divider: true,
			show: true
		},
		{  
			id: "address1",
			atts: {
				label: memberLabels.address1,
			},
			fw: true,
			show: memberSettings.show_address1,
			send: memberSettings.show_address1,
		},
		{ 
			id: "street",
			atts: {
				label: memberLabels.street,
				required: true,
			},
			fw: true,
			show: true,
			send: true,
		},
		{
			type: 'suburb',
			id: "suburb",
			atts: {
				label: memberLabels.suburb,
				required: true,
			},
			show: true,
			send: true,
		},
		{
			id: "postcode",
			atts: {
				label: memberLabels.postcode,
				required: true,
			},	
			show: true,
			send: true,
		},
		{
			type: "select",
			id: "state",
			atts: {
				label: memberLabels.state,
				required: true,
				options: 'states',
			},
			show: true,
			send: true,
		},
		{
			id: "country",
			atts: {
				label: "Country",
			},
			show: memberSettings.show_country,
			send: memberSettings.show_country,
		},
		{
			id: "address_divider",
			divider: true,
			show: true
		},
		{
			id: "email",
			atts: { 
				label: "Email",
				required: true,
				validate: 'email'
			},
			show: true,
			send: true,
		},
		{
			type: 'masked',
			id: "phone_home",
			atts: {
				label: memberLabels.phone_home,
				format:landlineMask,
			},
			show: true,
			send: true,
		},
		{
			type: 'masked',
			id: "phone_business",
			atts: {
				label: memberLabels.phone_business,
				format:landlineMask,
			},
			show: memberSettings.show_phone_business,
			send: memberSettings.show_phone_business,
		},
		{
			type: 'masked',
			id: "mobile",
			atts: {
				label: memberLabels.mobile,
				format:mobileMask,
			},
			show: true,
			send: true,
		},
		{
			id: "phone_divider",
			divider: true,
			show: true
		},
		{
			id: "birth_year",
			type: "numeric",
			atts: {
				label: "Year of birth",
				required: true,
			},		
			show: true,
			send: true,
		},
		{
			type: "select",
			id: "gender",
			atts: {
				label: "Gender",
				required: true,
				options: 'genders',
			},
			show: true,
			send: true,
		},
		{
			id: "yob_divider",
			divider: true,
			show: true
		},
		{
			id: "employer",
			atts: {
				label: memberLabels.employer,
			},
			show: memberSettings.show_employer,
			send: memberSettings.show_employer,
		},
		{
			id: "fax",
			atts: {
				label: memberLabels.fax,
			},
			show: memberSettings.show_fax,
			send: memberSettings.show_fax,
		},
		{
			id: "occupation",
			atts: {
				label: memberLabels.occupation,
				required: isTrue(memberSettings.require_occuption),
			}, 
			show: memberSettings.show_occupation,
			send: memberSettings.show_occupation,
		},
		{
			id: "registration",
			atts: {
				label: memberLabels.registration,
				required: (isTrue(memberSettings.require_registration))
			}, 
			show: memberSettings.show_registration,
			send: memberSettings.show_registration,
		},
		{
			id: "spare_field",
			atts: {
				label: memberLabels.spare_field,
			}, 
			show: memberSettings.show_spare_field,
			send: memberSettings.show_spare_field,
		},
		{
			id: "strings_divider",
			divider: true,
			show: (memberSettings.show_employer || memberSettings.show_fax || memberSettings.show_occupation
			|| memberSettings.show_registration || memberSettings.show_spare_field)
		},
		{
			id: "number1",
			type: "numeric",
			atts: {
				label: memberLabels.number1,
			},
			show: memberSettings.show_number1,
			send: memberSettings.show_number1,
		},
		{
			id: "number2",
			type: "numeric",
			atts: {
				label: memberLabels.number2,
			},
			show: memberSettings.show_number2,
			send: memberSettings.show_number2,
		},
		{
			id: "tickets",
			type: "numeric",
			atts: {
				label: memberLabels.tickets,
			},
			show: memberSettings.show_tickets,
			send: memberSettings.show_tickets,
		},
		{
			id: "numbers_divider",
			divider: true,
			show: (memberSettings.show_number1 || memberSettings.show_number2 || memberSettings.show_tickets)
		},
		{
			type: "select",
			id: "classification",
			atts: {
				label: memberLabels.classification,
				options: classificationOptions,
			},
			show: memberSettings.show_classification,
			send: memberSettings.show_classification,
		},
		{
			type: "select",
			id: "classifier1",
			atts: {
				label: memberLabels.classifier1,
				options: classifier1Options,
			},
			show: memberSettings.show_classifier1,
			send: memberSettings.show_classifier1,
		},
		{
			type: "select",
			id: "classifier2",
			atts: {
				label: memberLabels.classifier2,
				options: classifier2Options,
			},
			show: memberSettings.show_classifier2,
			send: memberSettings.show_classifier2,
		},
		{
			type: "select",
			id: "classifier3",
			atts: {
				label: memberLabels.classifier3,
				options: classifier3Options,
			},	
			show: memberSettings.show_classifier3,
			send: memberSettings.show_classifier3,
		},
		{
			id: "dropdowns_divider",
			divider: true,
			show: (memberSettings.show_classification || memberSettings.show_classifier1 ||
			memberSettings.show_classifier2 || memberSettings.show_classifier3)
		},
		{
			type: "date",
			id: "received_date",
			atts: {
				label: memberLabels.received_date,
				placeholder: memberLabels.received_date,
				format:"DD/MM/YYYY",
			},
			show: memberSettings.show_received_date,
			send: memberSettings.show_received_date,
		},
		{
			type: "date",
			id: "registration_date",
			atts: {
				label: memberLabels.registration_date,
				placeholder: memberLabels.registration_date,
				format:"DD/MM/YYYY",
				required: (isTrue(memberSettings.require_registration_date)),
			},
			show: memberSettings.show_registration_date,
			send: memberSettings.show_registration_date,
		},
		{
			id: "dates_divider",
			divider: true,
			show: (memberSettings.show_received_date || memberSettings.show_registration_date)
		},
		{
			atts: {
				label: memberLabels.checkbox1,
			},
			type: "checkbox",
			id: "checkbox1",
			show: memberSettings.show_checkbox1,
			send: memberSettings.show_checkbox1,
		},
		{
			atts: {
				label: memberLabels.checkbox2,
			},
			type: "checkbox",
			id: "checkbox2",
			show: memberSettings.show_checkbox2,
			send: memberSettings.show_checkbox2,
		},
		{
			atts: {
				label: memberLabels.checkbox3,
			},
			type: "checkbox",
			id: "checkbox3",
			show: memberSettings.show_checkbox3,
			send: memberSettings.show_checkbox3,
		},
		{
			atts: {
				label: memberLabels.checkbox4,
			},
			type: "checkbox",
			id: "checkbox4",
			show: memberSettings.show_checkbox4,
			send: memberSettings.show_checkbox4,
		},
		{
			id: "checkbox_divider",
			divider: true,
			show: (memberSettings.show_checkbox1 || memberSettings.show_checkbox2 ||
				memberSettings.show_checkbox3 || memberSettings.show_checkbox4)
		},
		{
			id: "emergency_contact",
			atts: {
				label: memberLabels.emergency_contact,
				required: true,
			},
			show: true,
			send: true,
		},
		{
			id: "emergency_number",
			atts: {
				label: memberLabels.emergency_number,
				required: true,
			},
			show: true,
			send: true,
		},
		{
			id: "emergency_relationship",
			atts: {
				label: memberLabels.emergency_relationship,
				required: (isTrue(memberSettings.require_emergency_relationship))
			},
			show: memberSettings.show_emergency_relationship,
			send: memberSettings.show_emergency_relationship,
		},
		{
			id: "emergency_divider",
			divider: true,
			show: true,
		},
		{
			atts: {
				label: 'Are you ' + memberLabels.fully_vaccinated + ' against COVID-19?',
			},
			type: "checkbox",
			id: "vaccinated",
			show: memberSettings.vaccine_rules_on,
			send: memberSettings.vaccine_rules_on,
		},
		{
			id: "vax_divider",
			divider: true,
			show: true,
		},
		{
			id: "notes",
			type: "read-only",
			atts: {
				label: "Member Notes",
				multiline: true,
				display_value: member.notes || '-',
			},
			fw: true,
			show: memberSettings.show_notes,
			send: false, 
		}
	];
	
	const maybeSubmit = () => {
		const filtered = _.chain(fields)
			.filter('send')
			.keyBy('id')
			.mapValues(f => {
				if(f.type === 'date'){
					return _.isEmpty(data[f.id]) ? data[f.id] : dayjs(data[f.id]).format('YYYY-MM-DD');
				} else {
					return data[f.id];
				}
			})
			.value();
		
		let s = yup.object().shape(schemaShape);
		
		s.validate(filtered, {abortEarly: false})
		.then((value) => {
			dispatch(updateMember(user.member_no, filtered));
			setFormStatus({
				dirty: false,
				error: false, 
				message: ''
			});
			
		})
		.catch((err) => {
			const copyV = _.cloneDeep(validator);
			_.each(err.inner, (e) => {
				copyV[e.path].error = true;
				copyV[e.path].message = e.message;
			});
			setValidator(copyV);
			setFormStatus({...formStatus, error: true, message: 'The form contains errors. Please correct them before proceeding.'});
		});
		
	}
	
	const Submitting = () => {
		if(fetchingMember){
			return <Waiting
				message='Fetching member details. Please wait...'
				/>
		} else if(updatingMember){
			return <Waiting
				message='Updating member details. Please wait...'
				/>
		} else {
			return <Waiting />
		}
	}
	
	return (
	<>
	{submitted ?
	<ErrorDisplay
	extra_button={{
		atts: {
			onClick: () => {
				resetForm();
				dispatch(resetErrorMessage());
			}
		},
		text: 'Reset form',
	}}
	/> : 
	<ErrorDisplay />
	}
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<PageTitle title='My Membership' />
	{ data.member_no === 0 || fetchingMember || updatingMember ? <Submitting/>
	: (
	<>
	<Typography variant="body2" paragraph>
	To update your details, simply edit the form below, and then click the SAVE button at the bottom of the form. You can also change your <Button variant="outlined" size="small" color="primary"
		onClick={() => setPasswordOpen(true)}
	>password</Button>{badgeAllowed ? <>, generate a <Button variant="outlined" size="small" color="primary"
		onClick={() => setNameBadgeOpen(true)}
	>name badge</Button>, </> : null} and view your <Link className={classes.noBreakAll} to="/member/enrolments">enrolments</Link>{generalSettings.absences_on ? <>, <Link className={classes.noBreakAll} to="/member/invoices">invoices</Link> and <Link className={classes.noBreakAll} to="/member/absences">absences</Link></> : <> and <Link className={classes.noBreakAll} to="/member/invoices">invoices</Link></>}.
	</Typography>
	<form className={classes.form} autoComplete="off" noValidate> 
	<Grid container spacing={2}>
		{fields.map((f, i) => {
			return f.show ? (
			f.divider ? (<Divider key={f.id} variant="middle" component='div' />) : (
			<Grid item key={f.id} xs={12} md={f.fw ? 12 : 6} className={classes.relative} >
			{ f.type && f.type === 'suburb' ?
			<SuburbSelector
				id={f.id}
				value={data[f.id]}
				error={validator[f.id].error}
				message={validator[f.id].message}
				handleChange={storeAndValidate}
				setSuburb={setSuburb}
				{...f.atts}
			/>
			:
			<FormField 
				type={f.type}
				id={f.id}
				value={data[f.id]}
				handleChange={storeAndValidate}
				error={validator[f.id] && validator[f.id].error}
				message={validator[f.id] ? validator[f.id].message : ''}
				{...f.atts}
			/>
			}
			</Grid>
			)) : null
		})}
	</Grid>
	</form>
	{formStatus.dirty ? 
	<div className={classes.formButtons}>
		<Button onClick={() => resetForm()} >
		Cancel
		</Button>
		<Button
			variant="contained"
			color="primary"
			onClick={() => maybeSubmit()}
		>
		Save
		</Button>
	</div>
	: null }
	<ChangePassword 
		passwordOpen={passwordOpen}
		setPasswordOpen={setPasswordOpen}
	/>
	{badgeAllowed ? 
	<GetNameBadge
		nameBadgeOpen={nameBadgeOpen}
		setNameBadgeOpen={setNameBadgeOpen}
	/>
	: null}
	<MemberLinks 
		current='member'
		absencesOn={generalSettings.absences_on}
		openPassword={setPasswordOpen}
		openNameBadge={badgeAllowed ? setNameBadgeOpen : false}
	/>
	<Snackbar
		anchorOrigin={{
			vertical: 'bottom',
			horizontal: 'center'
		}}
		open={formStatus.error}
		onClose={() => setFormStatus({...formStatus, error: false})}
		autoHideDuration={null}
	>
	<SnackbarContent
		aria-describedby='form-message'
		message={<span id="form-message" className={classes.snackMessage}>
			<ErrorIcon className={classes.errorSnack} />
			{formStatus.message}
		</span>}
		action={[
			<IconButton
			key="close"
			aria-label="close"
			color="inherit"
			onClick={() => setFormStatus({...formStatus, error: false})}
			>
			<CloseIcon />
			</IconButton>,
		]}
	/>
	</Snackbar>
	<CovidNotice />
	<GeneralNotice />
	</>
	)}
	</Paper>
	</>
)};

export default Member